import { isArray, nextTick } from '@rehaje/shared'

import { action } from './operations/register'
import { undispatch, undispatchDepth } from './undispatch'

import type { ObserveEvent, ObserverWithOptions, Reactive } from './types'
import type { Callback, TArray } from '@rehaje/shared'

export function dispatch<T extends ObserveEvent>(
  event: T,
  callback?: Callback
): T {
  if (undispatchDepth === 0) {
    action(() => internalDispatch(event.target, event))
  }
  if (typeof callback === 'function') {
    action(() => undispatch(callback))
  }
  return event
}

function internalDispatch(
  target: Reactive,
  event: ObserveEvent,
  path: (number | string)[] = [],
  parents: Reactive[] = []
) {
  target.__rr_version = event.__r_version

  const observers = target.__rr_observers
  if (observers !== undefined && observers.length > 0) {
    const asyncObservers: ObserverWithOptions[] = []
    event.path = path
    event.parents = parents
    event.currentTarget = target
    for (const observer of observers) {
      if (event.__r_isOriginExecuted(observer.origin)) continue
      else if (observer.async) asyncObservers.push(observer)
      else observer(event)
    }
    if (asyncObservers.length > 0) {
      nextTick(() => {
        event.path = path
        event.parents = parents
        event.currentTarget = target
        for (const observer of asyncObservers) {
          if (observer.async) {
            observer(event)
          } else {
            continue
          }
        }
        event.__r_runEffects()
      })
    }
  }

  const parentMaps = target.__rr_parents
  if (parentMaps !== undefined && parentMaps.size > 0) {
    for (const [parent, keys] of parentMaps) {
      if (parent.__rr_version === event.__r_version) continue
      else if (keys.length === 0) {
        parentMaps.delete(parent)
        continue
      }
      if (isArray(parent as Reactive)) {
        const rawTarget: Reactive = target.__rr_raw ?? target
        const rawParent: Reactive<TArray> = parent.__rr_raw ?? parent
        let needRefresh = false
        for (const key of keys) {
          const rawValue = rawParent[key].__rr_raw ?? rawParent[key]
          if (rawValue !== rawTarget) {
            needRefresh = true
            break
          }
        }
        if (needRefresh) {
          keys.length = 0
          for (const [key, value] of rawParent.entries()) {
            const rawValue = value?.__rr_raw ?? value
            if (rawValue === rawTarget) keys.push(String(key))
          }
        }
      }
      const nextParents = parents.concat(isArray(parent) ? [parent] : parent)
      for (const key of keys) {
        internalDispatch(parent, event, path.concat(key), nextParents)
      }
    }
  }
}
